export interface EngineCallbacks {
  fixedUpdate: (dt: number) => void;
  render: (alpha: number) => void;
}

export class Engine {
  private readonly FIXED_TIMESTEP = 1 / 60; // 60 Hz physics
  private readonly MAX_FRAME_TIME = 0.25; // Prevent spiral of death

  private callbacks: EngineCallbacks;
  private accumulator = 0;
  private lastTime = 0;
  private running = false;
  private animationFrameId: number | null = null;

  constructor(callbacks: EngineCallbacks) {
    this.callbacks = callbacks;
  }

  public start(): void {
    if (this.running) return;

    this.running = true;
    this.lastTime = performance.now() / 1000;
    this.loop();
  }

  public stop(): void {
    this.running = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  public isRunning(): boolean {
    return this.running;
  }

  private loop = (): void => {
    if (!this.running) return;

    const currentTime = performance.now() / 1000;
    let frameTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Clamp frame time to prevent spiral of death
    if (frameTime > this.MAX_FRAME_TIME) {
      frameTime = this.MAX_FRAME_TIME;
    }

    this.accumulator += frameTime;

    // Fixed timestep updates for physics
    while (this.accumulator >= this.FIXED_TIMESTEP) {
      this.callbacks.fixedUpdate(this.FIXED_TIMESTEP);
      this.accumulator -= this.FIXED_TIMESTEP;
    }

    // Render with interpolation factor
    const alpha = this.accumulator / this.FIXED_TIMESTEP;
    this.callbacks.render(alpha);

    this.animationFrameId = requestAnimationFrame(this.loop);
  };
}
